/**
 * DeliveryAdminRoute.jsx
 *
 * Route-level authorization guard for Assessment Delivery admin pages.
 * Independent of sidebar visibility — a student who types the URL directly
 * is redirected to "/" without exposing any delivery admin content.
 *
 * Canonical Phase 2B admin roles: super_admin, institution_admin, registrar,
 * programme_manager, dean.
 * Invigilator access comes from invigilation_assignment, not a global role.
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

const DELIVERY_ADMIN_ROLES = new Set([
  "super_admin",
  "institution_admin",
  "registrar",
  "programme_manager",
  "dean",
]);

export default function DeliveryAdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        className="flex h-screen items-center justify-center bg-slate-950"
        data-testid="delivery-admin-route-loading"
      >
        <div className="flex items-center gap-3 text-sm text-slate-400">
          <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
          Loading…
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!DELIVERY_ADMIN_ROLES.has(user.role)) {
    // Redirect silently — do not expose that the route exists
    return <Navigate to="/" replace />;
  }

  return children;
}
